import {
  blockerListSchema,
  blockerSchema,
  executionSettingsSchema,
  taskDetailSchema,
  taskPageSchema,
  type BlockerStatus,
  type BlockerView,
  type CancelBlockerRequest,
  type CompleteTaskRequest,
  type CreateTaskRequest,
  type OpenBlockerRequest,
  type ReasonedTaskRequest,
  type ReassignTaskRequest,
  type ResolveBlockerRequest,
  type TaskCollaboratorKind,
  type TaskSort,
  type TaskView,
  type TransitionTaskRequest,
  type UpdateExecutionSettingsRequest,
  type UpdateTaskRequest,
} from '@mfos/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, withQuery } from '../../lib/api-client';
import { workspaceKey, workspacePath } from '../workspace/workspace.api';
import { useWorkspaceMutation } from '../workspace/workspace-mutation';

const TASK_PAGE_SIZE = 25;

export interface TaskFilters {
  readonly view: TaskView;
  readonly sort?: TaskSort;
  /** Comma-separated statuses; empty = open statuses. */
  readonly status?: string;
  readonly overdue?: boolean;
  readonly q?: string;
}

export const tasksKey = (workspaceId: string) => [...workspaceKey(workspaceId), 'tasks'] as const;
export const taskKey = (workspaceId: string, taskId: string) =>
  [...workspaceKey(workspaceId), 'task', taskId] as const;
export const blockersKey = (workspaceId: string) =>
  [...workspaceKey(workspaceId), 'blockers'] as const;
export const executionSettingsKey = (workspaceId: string) =>
  [...workspaceKey(workspaceId), 'settings', 'execution'] as const;

export function useTasks(workspaceId: string, filters: TaskFilters, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...tasksKey(workspaceId), 'list', filters] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      apiGet(
        withQuery(workspacePath(workspaceId, 'tasks'), {
          view: filters.view,
          sort: filters.sort,
          status: filters.status,
          overdue: filters.overdue === undefined ? undefined : String(filters.overdue),
          q: filters.q,
          cursor: pageParam,
          limit: TASK_PAGE_SIZE,
        }),
        { schema: taskPageSchema, signal },
      ),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
  });
}

export function useTask(workspaceId: string, taskId: string, enabled = true) {
  return useQuery({
    queryKey: taskKey(workspaceId, taskId),
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'tasks', taskId), { schema: taskDetailSchema, signal }),
    enabled,
  });
}

export function useCreateTask(workspaceId: string) {
  return useWorkspaceMutation(workspaceId, (body: CreateTaskRequest) =>
    apiPost(workspacePath(workspaceId, 'tasks'), body, taskDetailSchema),
  );
}

/** Every task mutation answers with the refreshed task detail. */
export function useTaskMutations(workspaceId: string, taskId: string) {
  const path = (...segments: string[]) => workspacePath(workspaceId, 'tasks', taskId, ...segments);
  return {
    update: useWorkspaceMutation(workspaceId, (body: UpdateTaskRequest) =>
      apiPatch(path(), body, taskDetailSchema),
    ),
    transition: useWorkspaceMutation(workspaceId, (body: TransitionTaskRequest) =>
      apiPost(path('transition'), body, taskDetailSchema),
    ),
    complete: useWorkspaceMutation(workspaceId, (body: CompleteTaskRequest) =>
      apiPost(path('complete'), body, taskDetailSchema),
    ),
    reopen: useWorkspaceMutation(workspaceId, (body: ReasonedTaskRequest) =>
      apiPost(path('reopen'), body, taskDetailSchema),
    ),
    cancel: useWorkspaceMutation(workspaceId, (body: ReasonedTaskRequest) =>
      apiPost(path('cancel'), body, taskDetailSchema),
    ),
    archive: useWorkspaceMutation(workspaceId, (body: { version: number }) =>
      apiPost(path('archive'), body, taskDetailSchema),
    ),
    reassign: useWorkspaceMutation(workspaceId, (body: ReassignTaskRequest) =>
      apiPost(path('reassign'), body, taskDetailSchema),
    ),
    setCollaborator: useWorkspaceMutation(
      workspaceId,
      ({ memberId, kind }: { memberId: string; kind: TaskCollaboratorKind }) =>
        apiPut(path('collaborators', memberId), { kind }, taskDetailSchema),
    ),
    removeCollaborator: useWorkspaceMutation(workspaceId, (memberId: string) =>
      apiDelete(path('collaborators', memberId), taskDetailSchema),
    ),
    addChecklistItem: useWorkspaceMutation(workspaceId, (title: string) =>
      apiPost(path('checklist'), { title }, taskDetailSchema),
    ),
    toggleChecklistItem: useWorkspaceMutation(
      workspaceId,
      ({ itemId, isDone }: { itemId: string; isDone: boolean }) =>
        apiPatch(path('checklist', itemId), { isDone }, taskDetailSchema),
    ),
    addComment: useWorkspaceMutation(workspaceId, (body: string) =>
      apiPost(path('comments'), { body }, taskDetailSchema),
    ),
    deleteComment: useWorkspaceMutation(workspaceId, (commentId: string) =>
      apiDelete(path('comments', commentId), taskDetailSchema),
    ),
    addDependency: useWorkspaceMutation(workspaceId, (dependsOnTaskId: string) =>
      apiPost(path('dependencies'), { dependsOnTaskId }, taskDetailSchema),
    ),
    removeDependency: useWorkspaceMutation(workspaceId, (dependsOnTaskId: string) =>
      apiDelete(path('dependencies', dependsOnTaskId), taskDetailSchema),
    ),
    openBlocker: useWorkspaceMutation(workspaceId, (body: OpenBlockerRequest) =>
      apiPost(path('blockers'), body, blockerSchema),
    ),
  };
}

export type TaskMutations = ReturnType<typeof useTaskMutations>;

export function useBlockers(
  workspaceId: string,
  view: BlockerView,
  status: BlockerStatus,
  enabled = true,
) {
  return useQuery({
    queryKey: [...blockersKey(workspaceId), view, status] as const,
    queryFn: ({ signal }) =>
      apiGet(withQuery(workspacePath(workspaceId, 'blockers'), { view, status }), {
        schema: blockerListSchema,
        signal,
      }),
    enabled,
  });
}

export function useResolveBlocker(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ blockerId, body }: { blockerId: string; body: ResolveBlockerRequest }) =>
      apiPost(workspacePath(workspaceId, 'blockers', blockerId, 'resolve'), body, blockerSchema),
  );
}

export function useCancelBlocker(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ blockerId, body }: { blockerId: string; body: CancelBlockerRequest }) =>
      apiPost(workspacePath(workspaceId, 'blockers', blockerId, 'cancel'), body, blockerSchema),
  );
}

export function useExecutionSettings(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: executionSettingsKey(workspaceId),
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'settings', 'execution'), {
        schema: executionSettingsSchema,
        signal,
      }),
    enabled,
  });
}

export function useUpdateExecutionSettings(workspaceId: string) {
  return useWorkspaceMutation(workspaceId, (body: UpdateExecutionSettingsRequest) =>
    apiPut(workspacePath(workspaceId, 'settings', 'execution'), body, executionSettingsSchema),
  );
}
